import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContractsHub({ authFetch }) {
  const [config, setConfig] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [form, setForm] = useState({ client_id: '', client_name: '', client_company: '', client_email: '', client_whatsapp: '', template_type: 'Empresa', plan_id: 'emprendedor' });
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c1, c2] = await Promise.all([authFetch(`${API}/admin/contracts/config`), authFetch(`${API}/admin/contracts`)]);
      if (c1.ok) setConfig(await c1.json());
      if (c2.ok) setContracts((await c2.json()).contracts || []);
    } catch { setConfig((p) => p || { clients: [], catalog: [], plans: [], templates: [] }); }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  const pickClient = (cid) => {
    const c = config?.clients?.find((x) => x.client_id === cid);
    setForm((p) => ({ ...p, client_id: cid, client_name: c ? c.display_name : p.client_name, client_company: c ? (c.segment_label || '') : p.client_company }));
  };

  const generate = async () => {
    setBusy(true); setResult(null);
    try {
      const services = Object.entries(selected).filter(([, v]) => v.on).map(([sid, v]) => ({ service_id: sid, price_credits: v.credits }));
      const res = await authFetch(`${API}/admin/contracts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, services }) });
      const data = await res.json();
      if (res.ok) { setResult({ ok: true, ...data }); load(); }
      else setResult({ ok: false, error: data.detail || 'Error generando contrato' });
    } catch { setResult({ ok: false, error: 'Error de conexión' }); }
    setBusy(false);
  };

  if (!config) return <div className="flex items-center gap-2 p-8 text-gray-400" data-testid="contracts-loading"><Loader2 className="w-5 h-5 animate-spin" /> Cargando generador de contratos…</div>;

  const input = "w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-gray-500";
  return (
    <div className="grid gap-6 xl:grid-cols-2" data-testid="admin-contracts-hub">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <h3 className="text-lg font-black text-white">📄 Generar contrato</h3>
        <p className="mt-1 text-xs text-gray-400">Tarifas desde la Tabla Maestra de Rentabilidad. Regla de oro: nunca por debajo de coste.</p>
        <div className="mt-4 space-y-2.5">
          <select value={form.client_id} onChange={(e) => pickClient(e.target.value)} className={input} data-testid="contract-client-select">
            <option value="">— Cliente del CRM (o escribe manual) —</option>
            {config.clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.display_name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.client_name} onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))} placeholder="Nombre del cliente *" className={input} data-testid="contract-client-name" />
            <input value={form.client_company} onChange={(e) => setForm((p) => ({ ...p, client_company: e.target.value }))} placeholder="Empresa" className={input} data-testid="contract-client-company" />
            <input value={form.client_email} onChange={(e) => setForm((p) => ({ ...p, client_email: e.target.value }))} placeholder="Email (recibe el contrato)" className={input} data-testid="contract-client-email" />
            <input value={form.client_whatsapp} onChange={(e) => setForm((p) => ({ ...p, client_whatsapp: e.target.value }))} placeholder="WhatsApp" className={input} data-testid="contract-client-whatsapp" />
            <select value={form.template_type} onChange={(e) => setForm((p) => ({ ...p, template_type: e.target.value }))} className={input} data-testid="contract-template-select">
              {config.templates.map((t) => <option key={t} value={t}>Plantilla: {t}</option>)}
            </select>
            <select value={form.plan_id} onChange={(e) => setForm((p) => ({ ...p, plan_id: e.target.value }))} className={input} data-testid="contract-plan-select">
              {config.plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name} {p.price_usd_month ? `· $${p.price_usd_month}/mes` : ''}</option>)}
            </select>
          </div>
          <p className="pt-1 text-xs font-bold uppercase tracking-wider text-cyan-300">Anexo de precios — elige servicios</p>
          <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="contract-services-list">
            {config.catalog.map((s) => {
              const sel = selected[s.service_id] || { on: false, credits: s.price_credits };
              return (
                <label key={s.service_id} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${sel.on ? 'border-cyan-400/50 bg-cyan-400/10' : 'border-white/10 bg-white/[0.02]'}`} data-testid={`contract-service-${s.service_id}`}>
                  <input type="checkbox" checked={sel.on} onChange={(e) => setSelected((p) => ({ ...p, [s.service_id]: { ...sel, on: e.target.checked } }))} />
                  <span className="flex-1 text-gray-200">{s.name}</span>
                  <input type="number" min="1" value={sel.credits} onChange={(e) => setSelected((p) => ({ ...p, [s.service_id]: { ...sel, credits: Number(e.target.value) } }))} className="w-20 rounded-lg border border-white/15 bg-white/5 px-2 py-1 text-right text-xs text-white" />
                  <span className="w-24 text-right text-[11px] text-gray-400">cr · ${(sel.credits * 0.1).toFixed(2)}</span>
                  <span className={`w-14 text-right text-[11px] font-bold ${((1 - s.cost_usd / (sel.credits * 0.1)) * 100) < 50 ? 'text-amber-400' : 'text-emerald-400'}`}>{sel.credits > 0 ? `${((1 - s.cost_usd / (sel.credits * 0.1)) * 100).toFixed(0)}%` : '—'}</span>
                </label>
              );
            })}
          </div>
          <button onClick={generate} disabled={busy} className="w-full rounded-xl bg-cyan-400 py-3 text-sm font-black text-[#02040a] disabled:opacity-50" data-testid="contract-generate-btn">{busy ? 'Generando…' : '⚡ Generar contrato + enviar al cliente'}</button>
          {result && (
            <div className={`rounded-xl px-3 py-2.5 text-xs ${result.ok ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`} data-testid="contract-result">
              {result.ok ? (
                <>
                  <p className="font-bold">✅ {result.contract.contract_number} creado {result.email_sent ? '· email enviado al cliente' : '· sin email (añade email para enviarlo)'}</p>
                  {result.low_margin_services?.length > 0 && <p className="mt-1 text-amber-300">⚠️ Margen &lt;50%: {result.low_margin_services.join(', ')} (alerta enviada al CEO)</p>}
                  <p className="mt-1 break-all">Enlace de firma: {result.sign_url}</p>
                </>
              ) : <p className="font-bold">⛔ {result.error}</p>}
            </div>
          )}
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <h3 className="text-lg font-black text-white">🗂 Contratos archivados ({contracts.length})</h3>
        <div className="mt-3 max-h-[560px] space-y-2 overflow-y-auto" data-testid="contracts-list">
          {contracts.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={`contract-row-${c.contract_number}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-white">{c.contract_number} · {c.client?.name}</p>
                  <p className="text-[11px] text-gray-400">{c.template_type} · Plan {c.plan?.name} · {c.services?.length} servicios · {c.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${c.status === 'firmado' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>{c.status === 'firmado' ? '✍️ Firmado' : 'Pendiente'}</span>
                  <a href={`${API}/contracts/${c.id}/pdf?token=${c.sign_token}`} target="_blank" rel="noreferrer" className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-bold text-cyan-300 hover:bg-white/20" data-testid={`contract-pdf-${c.contract_number}`}>PDF</a>
                </div>
              </div>
            </div>
          ))}
          {!contracts.length && <p className="rounded-xl bg-white/[0.02] p-4 text-center text-sm text-gray-500">Aún no hay contratos. Genera el primero a la izquierda.</p>}
        </div>
      </div>
    </div>
  );
}
